import { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, AutocompleteProps } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTiers, clearTiersData } from "../../reduxStore/TiersSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { fromEvent } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { Tiers } from "../../models/models";
import { TypeTiers } from "../../constants/enums";

/**
 * Autocomplete générique de tiers.
 * - `type` fourni  → filtre sur ce type (équivalent ClientAutoC / FournisseurAutoC).
 * - `type` omis    → recherche TOUS les types (client + fournisseur + employé).
 *   Utilisé par le formulaire d'opération diverse, dont la cible peut être de
 *   n'importe quel type de tiers.
 */
interface TiersAutoCProps
  extends Partial<AutocompleteProps<Tiers, false, false, false>> {
  onTiersChange: (tiers: Tiers | null) => void;
  value?: Tiers | null;
  type?: TypeTiers;
  label?: string;
  required?: boolean;
}

const limit = 10;
const offset = 0;

// Libellé court du type de tiers — affiché à côté du nom uniquement en mode "tous types"
const TYPE_LABEL: Record<string, string> = {
  client: 'Client',
  fournisseur: 'Fournisseur',
  employe: 'Employé',
};

const TiersAutoC: React.FC<TiersAutoCProps> = ({
  onTiersChange,
  value,
  type,
  label = "Tiers",
  required,
  ...autocompleteProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { tiersData, loading } = useSelector((state: RootState) => state.TiersSlice);
  const [selected, setSelected] = useState<Tiers | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setSelected(value?.id ? value : null);
  }, [value]);

  useEffect(() => {
    if (inputRef.current) {
      const subscription = fromEvent(inputRef.current, "input")
        .pipe(
          debounceTime(500),
          switchMap((event: any) => {
            const nom = event.target.value;
            if (nom.length > 2) {
              // type ?? null → backend renvoie tous les types si non filtré
              dispatch(fetchTiers({ type: type ?? null, nom, limit, offset }));
            } else {
              dispatch(clearTiersData());
            }
            return [];
          })
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [dispatch, type]);

  const handleChange = (_: any, newValue: Tiers | null) => {
    setSelected(newValue);
    onTiersChange(newValue);
  };

  return (
    <Autocomplete
      options={tiersData}
      value={selected}
      getOptionLabel={(option: Tiers) => {
        const base = `${option.nom} ${option.prenom}`;
        // En mode "tous types" (pas de filtre type), on précise le type à côté du nom
        return !type && option.type ? `${base} — ${TYPE_LABEL[option.type] ?? option.type}` : base;
      }}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      loading={loading}
      onChange={handleChange}
      onBlur={() => dispatch(clearTiersData())}
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          variant="outlined"
          required={!!required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size="small"
        />
      )}
    />
  );
};

export default TiersAutoC;
